// FILE: src/services/staff-service.js
// NEW: Staff Attendance Report Services

import axios from "../shared/axios/index";
import { API_ROUTES } from "../config/api-config";

// ============================================================
// STAFF ATTENDANCE REPORT SERVICES
// ============================================================

/**
 * Get comprehensive staff report (today, 7D, 30D)
 * @param {Object} payload - { config_id?, date? }
 * @returns {Promise} Report data with three time periods
 */
export const getStaffReport = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_report}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching staff report:", error);
    throw error;
  }
};

/**
 * Get daily breakdown for last 7 days
 * @param {Object} payload - { config_id?, end_date? }
 * @returns {Promise} Daily breakdown data
 */
export const getStaffDailyBreakdown = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_daily_breakdown}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching daily breakdown:", error);
    throw error;
  }
};

/**
 * Get department-wise report for last 7 days
 * @param {Object} payload - { config_id?, end_date? }
 * @returns {Promise} Department report data
 */
export const getStaffDepartmentReport = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_department_report}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching department report:", error);
    throw error;
  }
};

/**
 * Get 30-day trend analysis with weekly breakdown
 * @param {Object} payload - { config_id?, end_date? }
 * @returns {Promise} Trend analysis data
 */
export const getStaffTrendAnalysis = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_trend_analysis}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching trend analysis:", error);
    throw error;
  }
};

// ============================================================
// EXISTING STAFF SERVICES (Keep these)
// ============================================================

/**
 * Get staff summary with pagination
 * @param {Object} payload - { config_id?, staff_name?, date?, page_number, page_size }
 * @returns {Promise} Staff summary data
 */
export const getStaffSummary = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_summary}`,
      payload
    );
    return response;
  } catch (error) {
    console.error("Error fetching staff summary:", error);
    throw error;
  }
};

/**
 * Get staff statistics (count aggregates)
 * @param {Object} payload - { config_id?, date?, start_date?, end_date? }
 * @returns {Promise} Staff statistics
 */
export const getStaffStats = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.get_staff_stats}`,
      payload
    );
    return response;
  } catch (error) {
    console.error("Error fetching staff stats:", error);
    throw error;
  }
};

/**
 * Download staff attendance PDF
 * @param {Object} payload - { config_id?, staff_name?, date? }
 * @returns {Promise} PDF blob
 */
export const downloadStaffPdf = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.download_staff_pdf}`,
      payload,
      {
        responseType: "blob",
      }
    );
    return response;
  } catch (error) {
    console.error("Error downloading staff PDF:", error);
    throw error;
  }
};

/**
 * Download staff attendance CSV
 * @param {Object} payload - { config_id?, staff_name?, date? }
 * @returns {Promise} CSV blob
 */
export const downloadStaffCsv = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.download_staff_csv}`,
      payload,
      {
        responseType: "blob",
      }
    );
    return response;
  } catch (error) {
    console.error("Error downloading staff CSV:", error);
    throw error;
  }
};

/**
 * Register new staff member
 * @param {Object} payload - Staff registration data
 * @returns {Promise} Response
 */
export const registerStaff = async (payload) => {
  try {
    const response = await axios.post(
      `${API_ROUTES.register_staff}`,
      payload
    );
    return response;
  } catch (error) {
    console.error("Error registering staff:", error);
    throw error;
  }
};

/**
 * Get all staff members
 * @returns {Promise} List of all staff
 */
export const getAllStaff = async () => {
  try {
    const response = await axios.get(`${API_ROUTES.get_all_staff}`);
    return response;
  } catch (error) {
    console.error("Error fetching all staff:", error);
    throw error;
  }
};

/**
 * Update staff member
 * @param {String} staff_id - Staff ID
 * @param {Object} payload - Updated staff data
 * @returns {Promise} Response
 */
export const updateStaff = async (staff_id, payload) => {
  try {
    const response = await axios.put(
      `${API_ROUTES.update_staff}/${staff_id}`,
      payload
    );
    return response;
  } catch (error) {
    console.error("Error updating staff:", error);
    throw error;
  }
};

/**
 * Delete staff member
 * @param {String} staff_id - Staff ID
 * @returns {Promise} Response
 */
export const deleteStaff = async (staff_id) => {
  try {
    const response = await axios.delete(
      `${API_ROUTES.delete_staff}/${staff_id}`
    );
    return response;
  } catch (error) {
    console.error("Error deleting staff:", error);
    throw error;
  }
};

/**
 * Get staff member by ID
 * @param {String} staff_id - Staff ID
 * @returns {Promise} Staff data
 */
export const getStaffById = async (staff_id) => {
  try {
    const response = await axios.get(`${API_ROUTES.get_staff}/${staff_id}`);
    return response;
  } catch (error) {
    console.error("Error fetching staff:", error);
    throw error;
  }
};